import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["offset", "setOffset"];
import { useEffect, useCallback, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { usageTracker } from '../utils/usageTracker';
import { DEFAULT_LIMIT, useTicketListings } from '../data/useTicketListings';
import { useHistory } from 'react-router-dom';
import { useDataFetchingClient } from 'data-fetching-client';
import { GET_TICKET } from '../data/useTicket';
import { GET_TICKET_THREADS } from '../data/useTicketThreads';
import { TicketListingsTable } from '../components/TicketListingsTable';
import { Flex } from 'foundations-components/components/Flex';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { TicketListingsGrid } from '../components/TicketListingsGrid';
import { Text } from 'foundations-components/components/Text';
import { useIsMobile } from '../hooks/useBreakpoint';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useHasConfiguredColumn } from '../utils/useHasConfiguredColumn';
import { useTicketListingsFilters } from '../hooks/useTicketListingsFilters';
import { useQueryParam } from '../utils/useQueryParam';
import { useCsvExport } from '../data/useCsvExport';
import { TicketListingsHeader } from '../components/TicketListingsHeader';
import { useIsPreview } from '../hooks/useIsPreview';
import { FAKE_THREAD_ID } from '../data/useTicketThreads';
import { useIsPillBorderRadius } from 'portals-platform-lib';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export let TicketListingView;
(function (TicketListingView) {
  TicketListingView["GRID"] = "grid";
  TicketListingView["TABLE"] = "table";
})(TicketListingView || (TicketListingView = {}));
export function TicketListings() {
  const isPreview = useIsPreview();
  const [view, setView] = useQueryParam('view', TicketListingView.TABLE);
  const _useTicketListingsFil = useTicketListingsFilters(),
    {
      offset,
      setOffset
    } = _useTicketListingsFil,
    ticketListingsFilters = _objectWithoutPropertiesLoose(_useTicketListingsFil, _excluded);
  const isMobile = useIsMobile();
  const isUsingPillBorderRadius = useIsPillBorderRadius();
  const {
    sort,
    setSort,
    ticketView,
    statusFilter,
    searchString,
    createDateFilter,
    lastActivityFilter
  } = ticketListingsFilters;
  const client = useDataFetchingClient();
  const history = useHistory();
  const ticketListings = useTicketListings({
    sort,
    offset,
    ticketView,
    statusFilter,
    searchString,
    createDateFilter,
    lastActivityFilter
  });
  const csvExport = useCsvExport({
    sort,
    ticketView,
    statusFilter,
    searchString,
    createDateFilter,
    lastActivityFilter
  });
  useEffect(() => {
    usageTracker.track('Customer Portal App', 'Page Viewed', {
      screen: 'ticket-listings'
    });
  }, []);
  const onRowClick = useCallback(ticketId => {
    if (ticketListings.loading) return;
    if (isPreview) {
      const {
        search,
        hash
      } = new URL(window.location.href);
      history.push(`/ticket/${ticketId}/${FAKE_THREAD_ID}${search}${hash}`);
      return;
    }
    history.push(`/ticket/${ticketId}`);
  }, [history, ticketListings.loading, isPreview]);
  const onRowHover = useCallback(ticketId => {
    if (ticketListings.loading || isPreview) return;
    void client.query({
      query: GET_TICKET,
      variables: {
        id: ticketId
      }
    });
    void client.query({
      query: GET_TICKET_THREADS,
      variables: {
        ticketId
      }
    });
  }, [client, ticketListings.loading, isPreview]);
  const data = useMemo(() => {
    if (ticketListings.loading || isPreview) {
      return new Array(DEFAULT_LIMIT).fill(null).map((_, index) => ({
        hs_ticket_id: index
      }));
    }
    if (!ticketListings.data) {
      return [];
    }
    return ticketListings.data;
  }, [ticketListings.loading, ticketListings.data, isPreview]);
  const hasCreatedateColumn = useHasConfiguredColumn('createdate');
  const showSortingHelper = useMemo(() => !hasCreatedateColumn && sort.includes('-createdate'), [hasCreatedateColumn, sort]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "INDEX_LOAD",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      paddingBlock: isMobile ? '400' : '800',
      paddingInline: isMobile ? '700' : '1400',
      children: [/*#__PURE__*/_jsx(TicketListingsHeader, {
        searchString: searchString,
        setSearchString: ticketListingsFilters.setSearchString,
        totalCount: ticketListings.total,
        csvExport: csvExport,
        view: view,
        setView: setView,
        ticketListingsFilters: ticketListingsFilters
      }), /*#__PURE__*/_jsxs("div", {
        children: [view === TicketListingView.GRID || isMobile ? /*#__PURE__*/_jsx(TicketListingsGrid, {
          data: data,
          isLoading: ticketListings.loading || isPreview,
          onRowClick: onRowClick,
          onRowHover: onRowHover,
          isUsingPillBorderRadius: isUsingPillBorderRadius
        }) : /*#__PURE__*/_jsx(TicketListingsTable, {
          data: data,
          isLoading: ticketListings.loading || isPreview,
          onRowClick: onRowClick,
          onRowHover: onRowHover,
          sort: sort,
          setSort: setSort,
          isUsingPillBorderRadius: isUsingPillBorderRadius
        }), showSortingHelper && /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "200",
          textAlign: "end",
          typography: "detail-200",
          color: "text-core-default",
          "data-test-id": "sorting-helper",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.sortingHelper"
          })
        })]
      }), ticketListings.total > DEFAULT_LIMIT && /*#__PURE__*/_jsx(Paginator, {
        "data-test-id": "listings-paginator",
        marginBlockStart: "600",
        pageCount: Math.ceil(ticketListings.total / DEFAULT_LIMIT),
        page: Math.floor(offset / DEFAULT_LIMIT) + 1,
        maxVisiblePageButtons: 7,
        onPageChange: e => setOffset((e.target.value - 1) * DEFAULT_LIMIT)
      })]
    })
  });
}